import { Box, Tab, Tabs, useTheme, Select, MenuItem, Typography, makeStyles, Theme, createStyles } from "@material-ui/core";
import React from "react";
import SwipeableViews from "react-swipeable-views";
import { APIDeployment, EventDeployment, Version } from "../products/components/Product.model";
import { TabContext, TabPanel } from "@material-ui/lab";
import { APIServiceEndpoints, EventServiceEndpoints } from "../products/components/ProductServices";
import APIReference from "../consumer/components/products/product/apireference/APIReference";
import EventsSpecReference from "../consumer/components/products/product/apireference/EventsSpecReference";
import QualitativeMetrics from "../consumer/components/products/product/apireference/QualitativeMetrics";
import LintingMetrics from "../consumer/components/products/product/apireference/LintingMetrics";
import { useCurrentTeam } from "libs/security/authorization/Permission";
import { useSelector } from "react-redux";
import ServiceSubscriptionView from "./ServiceSubscriptionView";

interface ServiceVersionProps {
    productName: string
    version: Version;
    type: string;
    serviceName: string;
    scope?: string;
}

const useTabStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabContainer: {
      marginBottom: theme.spacing(2),
    },
    tabs: {
      marginBottom: theme.spacing(2),
    },
    tab: {
      fontSize: '14px',
      fontWeight: 600,
    },
    nestedTabs: {
      marginBottom: theme.spacing(2),
    },
    tabPanel: {
      padding: '0px',
    }
  })
);

export const ServiceVersionDetails:React.FC<ServiceVersionProps> = ({productName, version, type, serviceName, scope}) => {

    const [value, setValue] = React.useState(0);
    const classes = useTabStyles();
    const { teamId: storedTeamId } = useCurrentTeam();
    const teamId = useSelector((state: any) =>state?.teamManager?.teams?.currentTeam?.id ?? storedTeamId);
    const handleChangeIndex = (value: any) => {
        setValue(value);
    };
    
    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const theme = useTheme();

    return (
        <TabContext value={value as any}>
            <div className={classes.tabContainer}>
                <Tabs
                    value={value}
                    onChange={handleTabChange}
                    aria-label="service version tabs"
                    indicatorColor="primary"
                    textColor="primary"
                    className={classes.tabs}
                >
                    <Tab 
                        value={0} 
                        label="Summary"
                        className={classes.tab}
                    />
                    <Tab 
                        value={1} 
                        label="Specification"
                        className={classes.tab}
                    />
                    {version?.teamId === teamId && (
                        <Tab 
                        value={2} 
                        label="Subscription"
                        className={classes.tab}
                    />)}
                </Tabs>
            </div>
            <SwipeableViews
                key={`${version?.versionName}-${type}`}
                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                index={value}
                onChangeIndex={handleChangeIndex}
            >
                <TabPanel value={0 as any} tabIndex={0} key={"SUMMARY"} className={classes.tabPanel}>
                    {type === "api" ? (

                        <APIDeployments apiDeployments={version?.apiDeployments} serviceName={serviceName} applications={version?.applications} scope={scope}/>
                    ) : (
                        <EventDeployments eventDeployments={version?.eventDeployments} />
                    )}
                </TabPanel>
                <TabPanel value={1 as any} tabIndex={1} key={"SPECIFICATION"} className={classes.tabPanel}>
                {type === "api" ?  (
                            (
                                <APISpecification
                                    key={`api-spec-${version?.versionName}`}
                                    productName={productName}
                                    prodSpecUri={version?.apiSpecProdUri ?? ''}
                                    preprodSpecUri={version?.apiSpecPreprodUri ?? ''}
                                    apiSpecUri={version?.apiSpecUri ?? ''}
                                />
                            )
                    ) : (
                        <EventSpecification 
                            key={`event-spec-${version?.versionName}`}
                            productName={productName} 
                            specUri={version?.eventSpecUri ?? ''} 
                        />
                    )}
                </TabPanel>
                {version?.teamId === teamId && (
                    <TabPanel value={2 as any} tabIndex={2} key={"SUBSCRIPTION"} className={classes.tabPanel}>
                        <ServiceSubscriptionView 
                            key={`subscription-${version?.versionName}`}
                            productName={productName} 
                        />
                    </TabPanel>
                )}
            </SwipeableViews>
        </TabContext>
    );
};

 

interface APIDeploymentProps {
    apiDeployments?: APIDeployment[];
    serviceName: string;
    applications: any[];
    scope?: string;
}
export const APIDeployments: React.FC<APIDeploymentProps> = ({ apiDeployments , serviceName, applications, scope}) => {
    const instancesLength = apiDeployments?.reduce((acc, deployment) => acc + (deployment.instances?.length || 0), 0) || 0;
    const isDeploymentsUnavailable = !apiDeployments || apiDeployments.length === 0 || instancesLength === 0;

    return (
        <>
            {!isDeploymentsUnavailable && apiDeployments && <APIServiceEndpoints apiDeployments={apiDeployments} applications={applications} serviceName={serviceName} scope={scope} />}
            {isDeploymentsUnavailable && (
                <Box m={1} p={3} border="1px solid #000">
                    Service not available for the selected configuration
                </Box>
            )}
        </>
    );
};

interface EventDeploymentProps {
    eventDeployments?: EventDeployment[];
}
export const EventDeployments:React.FC<EventDeploymentProps> = ({eventDeployments}) => {
    return (
        <>
            {(eventDeployments && eventDeployments?.length > 0 )&& (<EventServiceEndpoints eventDeployments={eventDeployments} />)}
            {(!eventDeployments || eventDeployments?.length === 0) && (<Box m={1} p={3} border="1px solid #000">
                No Deployments Available
            </Box>)}
        </>
    );
}

interface EventSpecProps {
    productName: string;
    specUri: string;
}

interface APISpecProps {
    productName: string;
    prodSpecUri: string;
    preprodSpecUri: string;
    apiSpecUri?: string;
}

export const APISpecification:React.FC<APISpecProps> = ({productName, prodSpecUri, preprodSpecUri, apiSpecUri}) => {
    const specDetails: any = {};

    if (preprodSpecUri || apiSpecUri) {
        specDetails.preProd = { fileName: apiSpecUri ? apiSpecUri: preprodSpecUri };
    }

    if (prodSpecUri || apiSpecUri) {
        specDetails.prod = { fileName: apiSpecUri? apiSpecUri : prodSpecUri };
    }

    return (
        <Box>
            <APIReference
                specDetails={specDetails}
                redocServerUrl=" "
                specUrl={process.env.REACT_APP_SPEC_URL as string}
                productName={productName} />
        </Box>
    );
}

export const EventSpecification:React.FC<EventSpecProps> = ({productName, specUri}) => {
    const specDetails: any = {};

    if (specUri) {
        specDetails.prod = { fileName: specUri };
    }

    return (
        <Box>
            { <EventsSpecReference
                specDetails={specDetails}
                redocServerUrl=" "
                specUrl={process.env.REACT_APP_EVENT_SPEC_URL as string}
                productName={productName} /> }
        </Box>
    );
}